// kadmon's orb in voice mode — 16 Sep 2026, at Natanel's word: while she listens the orb should look
// as if what the recruiter says is reaching it, and while she answers as if she is pushing the answer
// out, the way the frontier models' voice modes show that they hear and speak. His second word, the
// same night: a bubble, sleek, like ChatGPT's and pit.com's — not particles and rings, which read as
// childish. His third, the morning after: more kadmon, and closer still to Pit's. So it is a glass
// bubble with Pit's shimmer — a pearl body under a soap film coloured over its whole surface, richest
// at the edge (a ring of colour round an empty middle read as a hole) — and the shimmer runs through
// the accent's own hues (T392a: derived from the chosen accent by `orbHues`, no longer a fixed
// coral, rose, plum and lilac).
// And the funnel's radar is in it: a sweep of light turns through the glass while she thinks.
//
// WHAT MOVES IT. Two levels from 0 to 1, read every frame: the recruiter's voice as the microphone
// hears it, and hers as it plays (audio-level.ts reads both off Web Audio analysers). A syllable's
// onset is a level rising fast over its own recent average, so the motion lands on the words, not on
// a loop. Where a browser gives no levels, `speechLike` (audio-level.ts) stands in.
//
// WHAT IT DOES, state by state:
//   - listening: the shimmer's bands are drawn in from the rim towards the centre, and each stressed
//     syllable dents the rim where it lands and the bubble gives a little, as if taking a tap.
//   - thinking: the radar's sweep of light turns through the glass, and the bubble breathes.
//   - speaking: the bubble swells with her voice, the bands stream outward, the rim bulges on each
//     syllable and a faint edge glow rises round it.
//   - connecting: calm, dim, the sweep turning slowly as if searching. Muted: still, and greyed.
//
// WHY A MODEL AND TWO PAINTERS. `OrbMotion` is the motion with no DOM: levels and states in, one
// `OrbFrame` out — the bubble's size, how much its rim wobbles, which way and how far its shimmer has
// flowed, how bright the sweep is, its dents and bulges. orb.test.ts drives it on a seeded random. The frame is painted by the
// WebGL bubble (orb-gl.ts) where the browser has WebGL, and by `drawOrb2d` below where it does not —
// the same shape (`bubbleRadius`), simpler glass. Distances are fractions of the canvas's half-side,
// so the orb at the top of the stage, the big one in its middle and one on a test page are the
// same drawing.

export type OrbState = 'connecting' | 'listening' | 'thinking' | 'speaking' | 'muted';

/** How loud each side is right now, 0 to 1. */
export interface OrbLevels {
  /** The recruiter, as the microphone hears them. */
  input: number;
  /** kadmon, as her voice plays. */
  output: number;
}

/** The colours the theme gives the orb, read off its tokens (`paletteOf`). */
export interface OrbPalette {
  /** `--accent-ink`. */
  ink: string;
  /** `--accent-bright`. */
  bright: string;
  /** `--accent`: the chosen accent. */
  accent: string;
  /** `--accent-deep`. */
  deep: string;
  /** `--ink-3`: what a muted bubble greys towards. */
  line: string;
  /** `--bg-2`: what the bubble sits on — the glass is drawn for a dark ground or a light one. */
  surface: string;
}

/** The light-green theme's values (styles/tokens.css), used when a token cannot be read. */
export const ORB_FALLBACK_PALETTE: OrbPalette = {
  ink: '#FFFFFF',
  bright: '#2A5446',
  accent: '#1E3D33',
  deep: '#162D26',
  line: '#6F6F69',
  surface: '#FFFFFF',
};

/**
 * Whether a still orb (reduced motion) must be painted again: its state, its colours or the canvas
 * side in device pixels moved (T433; the side T433a: a zoom or another display changes
 * `devicePixelRatio`, and the 22px brand orb would blur at the old one).
 */
export function stillFrameStale(
  drawn: { state: OrbState; palette: OrbPalette; side: number } | null,
  now: { state: OrbState; palette: OrbPalette; side: number },
): boolean {
  if (!drawn || drawn.state !== now.state || drawn.side !== now.side) return true;
  return (Object.keys(now.palette) as (keyof OrbPalette)[]).some(
    (key) => drawn.palette[key] !== now.palette[key],
  );
}

/**
 * Calls `onChange` when `devicePixelRatio` changes with no resize to say so (T442a): the window
 * dragged to another display. A `resolution` query matches one ratio, so it fires once and is
 * armed again on the new ratio. Returns the way to stop.
 */
export function watchPixelRatio(win: Window, onChange: () => void): () => void {
  let stopped = false;
  let disarm = () => {};
  const arm = () => {
    const list = win.matchMedia(`(resolution: ${win.devicePixelRatio}dppx)`);
    const changed = () => {
      disarm();
      if (stopped) return;
      onChange();
      arm();
    };
    list.addEventListener('change', changed);
    disarm = () => list.removeEventListener('change', changed);
  };
  arm();
  return () => {
    stopped = true;
    disarm();
  };
}

/** The six colours the bubble is painted in, all derived from the theme's accent (T392a). */
export interface OrbHues {
  accent: string;
  bright: string;
  deep: string;
  /** The accent washed towards white: the film's pale band. */
  tint: string;
  /** A near-white with a breath of the accent: the body's pearl and a dark ground's rim. */
  pearl: string;
  /**
   * The deep accent darkened: the film's seam in the shader (which paints a light ground's
   * rim from `deep` and `pearl`), and a light ground's rim in the 2D fallback `drawOrb2d`.
   */
  shade: string;
}

/**
 * The bubble's hues for a palette. Since T392a they come from the accent alone — green, blue or red
 * from Settings — where the funnel's coral, rose, plum and lilac were fixed: so a chosen accent
 * recolours the orb, and a light ground gets a bubble that reads on white.
 */
export function orbHues(palette: OrbPalette, dark: boolean): OrbHues {
  return {
    accent: palette.accent,
    bright: palette.bright,
    deep: palette.deep,
    tint: mixColours(palette.accent, '#FFFFFF', dark ? 0.45 : 0.7),
    pearl: mixColours('#FFFFFF', palette.accent, dark ? 0.08 : 0.05),
    shade: mixColours(palette.deep, '#000000', 0.35),
  };
}

/** `from` taken `amount` (0 to 1) of the way to `to`, as hex; `from` as given when either will not parse. */
export function mixColours(from: string, to: string, amount: number): string {
  const a = rgbOf(from);
  const b = rgbOf(to);
  if (!a || !b) return from.trim();
  const hex = (i: number) =>
    Math.round(a[i]! + (b[i]! - a[i]!) * clamp01(amount))
      .toString(16)
      .padStart(2, '0');
  return `#${hex(0)}${hex(1)}${hex(2)}`;
}

/** The bubble's radius at rest, as a fraction of the canvas's half-side: 110px across in 196. */
export const ORB_RADIUS = 0.56;
/** Dents and bulges the rim holds at once — the shader's array length (orb-gl.ts). */
export const ORB_IMPULSES = 6;

/** A dent (strength below zero) or a bulge on the rim, springing back as it ages. */
export interface OrbImpulse {
  /** Where on the rim, in radians, 0 to the right and increasing anticlockwise. */
  angle: number;
  /** Its size this frame, as a fraction of the radius; already sprung. */
  strength: number;
  /** How wide round the rim, in radians. */
  width: number;
}

/** Everything a painter needs for one frame. */
export interface OrbFrame {
  /** 1 at rest; below while taking a tap, above while speaking. */
  scale: number;
  /** How far the rim's slow lobes reach, as a fraction of the radius. */
  wobble: number;
  /** Seconds of morph: the rim's lobes and the cloud's shape move on this clock. */
  time: number;
  /** How far the cloud has streamed, in zoom octaves: rising flows out, falling flows in. */
  flow: number;
  /** How far the cloud has turned, in radians. */
  swirl: number;
  /** The soft glow round the bubble, 0 to 1. */
  glow: number;
  /** How lit the shimmer is, about 0.6 to 1.2. */
  light: number;
  /** The radar's sweep of light through the glass, 0 to 1: full while she thinks. */
  sweep: number;
  /** 0 in full colour, 1 muted. */
  dim: number;
  impulses: readonly OrbImpulse[];
}

/** What the tests read. */
export interface OrbSnapshot {
  state: OrbState;
  scale: number;
  wobble: number;
  glow: number;
  sweep: number;
  dim: number;
  /** The shimmer's flow this frame, in bands a second: below zero inward, above outward. */
  flowSpeed: number;
  /** Radians a second. */
  swirlSpeed: number;
  /** Onsets heard from the recruiter, and pushed out by kadmon, since the orb was made. */
  taps: number;
  pushes: number;
  /** The deepest dent and the largest bulge standing on the rim. */
  deepestDent: number;
  largestBulge: number;
}

const TAU = Math.PI * 2;
/** The longest step taken in one go: a tab that slept resumes, it does not jump. */
const MAX_STEP_MS = 64;
const INPUT_ONSET = { floor: 0.2, rise: 0.09, gapS: 0.12 } as const;
const OUTPUT_ONSET = { floor: 0.16, rise: 0.07, gapS: 0.14 } as const;
/** How an impulse springs back: it decays over this many seconds, ringing at this rate. */
const IMPULSE_DECAY_S = 0.32;
const IMPULSE_RING = 13;

interface Standing {
  angle: number;
  size: number;
  width: number;
  age: number;
}

/** Where each state settles, before the voice moves it. */
interface Rest {
  scale: number;
  breathe: number;
  breatheS: number;
  wobble: number;
  flow: number;
  swirl: number;
  glow: number;
  light: number;
  sweep: number;
  dim: number;
}

const REST: Record<OrbState, Rest> = {
  connecting: {
    scale: 0.92,
    breathe: 0.02,
    breatheS: 2.4,
    wobble: 0.014,
    flow: 0.04,
    swirl: 0.12,
    glow: 0.12,
    light: 0.75,
    sweep: 0.55,
    dim: 0.35,
  },
  listening: {
    scale: 1,
    breathe: 0.014,
    breatheS: 3,
    wobble: 0.016,
    flow: -0.05,
    swirl: 0.08,
    glow: 0.16,
    light: 0.9,
    sweep: 0.08,
    dim: 0,
  },
  thinking: {
    scale: 0.98,
    breathe: 0.028,
    breatheS: 1.5,
    wobble: 0.022,
    flow: 0,
    swirl: 0.85,
    glow: 0.22,
    light: 0.95,
    sweep: 1,
    dim: 0,
  },
  speaking: {
    scale: 1,
    breathe: 0.01,
    breatheS: 2,
    wobble: 0.018,
    flow: 0.08,
    swirl: 0.12,
    glow: 0.2,
    light: 0.95,
    sweep: 0.06,
    dim: 0,
  },
  muted: {
    scale: 0.95,
    breathe: 0,
    breatheS: 3,
    wobble: 0.008,
    flow: 0,
    swirl: 0.02,
    glow: 0.04,
    light: 0.6,
    sweep: 0,
    dim: 1,
  },
};

const clamp01 = (v: number): number => (Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0);

/** A follower: quick to rise, slower to fall, or the other way round. */
function follow(
  current: number,
  target: number,
  dtS: number,
  riseS: number,
  fallS: number,
): number {
  const tau = target > current ? riseS : fallS;
  return current + (target - current) * (1 - Math.exp(-dtS / tau));
}

export class OrbMotion {
  readonly #random: () => number;
  #state: OrbState = 'connecting';
  #clock = 0;
  #inFast = 0;
  #inSlow = 0;
  #outFast = 0;
  #outSlow = 0;
  #lastIn = -Infinity;
  #lastOut = -Infinity;
  #give = 0;
  #kick = 0;
  #scale = REST.connecting.scale;
  #wobble = REST.connecting.wobble;
  #glow = REST.connecting.glow;
  #light = REST.connecting.light;
  #sweep = REST.connecting.sweep;
  #dim = REST.connecting.dim;
  #flowSpeed = REST.connecting.flow;
  #swirlSpeed = REST.connecting.swirl;
  #time = 0;
  #flow = 0;
  #swirl = 0;
  #standing: Standing[] = [];
  #taps = 0;
  #pushes = 0;

  constructor(random: () => number = Math.random) {
    this.#random = random;
  }

  get state(): OrbState {
    return this.#state;
  }

  /** Advance the motion by one frame. */
  step(dtMs: number, state: OrbState, levels: OrbLevels): void {
    const dt = Math.min(Math.max(Number.isFinite(dtMs) ? dtMs : 0, 0), MAX_STEP_MS) / 1000;
    if (state !== this.#state) this.#enter(state);
    this.#clock += dt;

    const input = state === 'listening' ? clamp01(levels.input) : 0;
    const output = state === 'speaking' ? clamp01(levels.output) : 0;
    this.#inFast = follow(this.#inFast, input, dt, 0.018, 0.09);
    this.#inSlow = follow(this.#inSlow, input, dt, 0.16, 0.4);
    this.#outFast = follow(this.#outFast, output, dt, 0.025, 0.14);
    this.#outSlow = follow(this.#outSlow, output, dt, 0.16, 0.4);

    if (
      state === 'listening' &&
      this.#inFast > INPUT_ONSET.floor &&
      this.#inFast - this.#inSlow > INPUT_ONSET.rise &&
      this.#clock - this.#lastIn > INPUT_ONSET.gapS
    ) {
      // A tap: the rim dents where it lands and the bubble gives.
      this.#lastIn = this.#clock;
      this.#taps += 1;
      const strength = Math.min(1, this.#inFast * 1.3);
      this.#give = Math.min(1, this.#give + 0.55 * strength);
      this.#impulse(-(0.035 + 0.04 * strength), 0.5 + 0.2 * this.#random());
    }
    if (
      state === 'speaking' &&
      this.#outFast > OUTPUT_ONSET.floor &&
      this.#outFast - this.#outSlow > OUTPUT_ONSET.rise &&
      this.#clock - this.#lastOut > OUTPUT_ONSET.gapS
    ) {
      // A push: the rim bulges and the bubble swells a touch past her level.
      this.#lastOut = this.#clock;
      this.#pushes += 1;
      const strength = Math.min(1, this.#outFast * 1.25);
      this.#kick = Math.min(1, this.#kick + 0.45 * strength);
      this.#impulse(0.03 + 0.035 * strength, 0.6 + 0.25 * this.#random());
    }

    const rest = REST[state];
    const breath = rest.breathe * Math.sin((this.#clock * TAU) / rest.breatheS);
    const voice = state === 'listening' ? this.#inFast : state === 'speaking' ? this.#outFast : 0;
    const target = {
      scale:
        rest.scale +
        breath -
        0.07 * this.#give +
        (state === 'speaking' ? 0.13 * this.#outFast + 0.05 * this.#kick : 0),
      wobble: rest.wobble + 0.035 * voice,
      glow: rest.glow + (state === 'speaking' ? 0.55 : 0.25) * voice,
      light: rest.light + 0.25 * voice,
      flow:
        state === 'listening'
          ? rest.flow - 0.85 * this.#inFast
          : state === 'speaking'
            ? rest.flow + 1.05 * this.#outFast
            : rest.flow,
      swirl: rest.swirl,
    };
    // The give lands at once, and every other change eases.
    this.#scale =
      target.scale < this.#scale && this.#give > 0.05
        ? target.scale
        : follow(this.#scale, target.scale, dt, 0.06, 0.1);
    this.#wobble = follow(this.#wobble, target.wobble, dt, 0.08, 0.3);
    this.#glow = follow(this.#glow, target.glow, dt, 0.06, 0.35);
    this.#light = follow(this.#light, target.light, dt, 0.06, 0.3);
    this.#sweep = follow(this.#sweep, rest.sweep, dt, 0.35, 0.45);
    this.#dim = follow(this.#dim, rest.dim, dt, 0.25, 0.25);
    this.#flowSpeed = follow(this.#flowSpeed, target.flow, dt, 0.12, 0.35);
    this.#swirlSpeed = follow(this.#swirlSpeed, target.swirl, dt, 0.4, 0.6);

    this.#give *= Math.exp(-dt / 0.14);
    this.#kick *= Math.exp(-dt / 0.2);
    this.#time += dt * (1 + 1.4 * voice);
    this.#flow += this.#flowSpeed * dt;
    this.#swirl += this.#swirlSpeed * dt;
    for (const standing of this.#standing) standing.age += dt;
    this.#standing = this.#standing.filter((s) => s.age < IMPULSE_DECAY_S * 5);
  }

  /** The frame to paint. */
  frame(): OrbFrame {
    return {
      scale: this.#scale,
      wobble: this.#wobble,
      time: this.#time,
      flow: this.#flow,
      swirl: this.#swirl,
      glow: this.#glow,
      light: this.#light,
      sweep: this.#sweep,
      dim: this.#dim,
      impulses: this.#standing.map((s) => ({
        angle: s.angle,
        strength: s.size * sprung(s.age),
        width: s.width,
      })),
    };
  }

  inspect(): OrbSnapshot {
    const impulses = this.frame().impulses;
    return {
      state: this.#state,
      scale: this.#scale,
      wobble: this.#wobble,
      glow: this.#glow,
      sweep: this.#sweep,
      dim: this.#dim,
      flowSpeed: this.#flowSpeed,
      swirlSpeed: this.#swirlSpeed,
      taps: this.#taps,
      pushes: this.#pushes,
      deepestDent: Math.min(0, ...impulses.map((i) => i.strength)),
      largestBulge: Math.max(0, ...impulses.map((i) => i.strength)),
    };
  }

  #enter(next: OrbState): void {
    const was = this.#state;
    this.#state = next;
    if (was === 'listening' && next === 'thinking') {
      // Taken in: one last give, all round.
      this.#give = Math.min(1, this.#give + 0.5);
    }
    if (next === 'speaking') this.#kick = Math.min(1, this.#kick + 0.6);
    if (next === 'muted') this.#standing = [];
  }

  #impulse(size: number, width: number): void {
    this.#standing.push({ angle: this.#random() * TAU, size, width, age: 0 });
    // The oldest go first: the shader holds a fixed number.
    if (this.#standing.length > ORB_IMPULSES)
      this.#standing.splice(0, this.#standing.length - ORB_IMPULSES);
  }
}

/** How much of an impulse stands after `age` seconds: a quick spring back with a little ring. */
function sprung(age: number): number {
  return Math.exp(-age / IMPULSE_DECAY_S) * Math.cos(age * IMPULSE_RING);
}

/**
 * The rim's radius at an angle, as a fraction of the canvas's half-side — the shape both painters
 * draw (orb-gl.ts writes the same sum in GLSL; keep the two in step).
 */
export function bubbleRadius(frame: OrbFrame, angle: number): number {
  const t = frame.time;
  let r =
    1 +
    frame.wobble *
      (0.55 * Math.sin(3 * angle + t * 0.9) +
        0.35 * Math.sin(2 * angle - t * 1.3 + 1.7) +
        0.25 * Math.sin(5 * angle + t * 1.7 + 0.6));
  for (const impulse of frame.impulses) {
    const d = Math.atan2(Math.sin(angle - impulse.angle), Math.cos(angle - impulse.angle));
    r += impulse.strength * Math.exp(-(d * d) / (impulse.width * impulse.width));
  }
  return r * frame.scale * ORB_RADIUS;
}

/**
 * Whether the bubble sits on a dark ground: the surface token's lightness, below the middle. An
 * unreadable surface counts as light, the brain's default since T392a.
 */
export function onDarkSurface(palette: OrbPalette): boolean {
  const rgb = rgbOf(palette.surface);
  if (!rgb) return false;
  const [r, g, b] = rgb.map((c) => c / 255) as [number, number, number];
  return 0.299 * r + 0.587 * g + 0.114 * b < 0.5;
}

/**
 * The bubble without WebGL: the same rim as simpler glass — a faint edge glow, a milky inside, a band
 * of the accent's hues round the rim, and a streak of light up and to the left. `ctx` is already
 * scaled to CSS pixels; `size` is the canvas's side in them.
 */
export function drawOrb2d(
  ctx: CanvasRenderingContext2D,
  size: number,
  frame: OrbFrame,
  palette: OrbPalette,
): void {
  ctx.clearRect(0, 0, size, size);
  const half = size / 2;
  const dark = onDarkSurface(palette);
  const hues = orbHues(palette, dark);
  const muted = frame.dim > 0.5;
  const alpha = 1 - 0.4 * frame.dim;
  const body = frame.scale * ORB_RADIUS * half;

  const glow = ctx.createRadialGradient(half, half, body * 0.7, half, half, half * 0.98);
  const glowColour = muted ? palette.line : palette.accent;
  glow.addColorStop(0, withAlpha(glowColour, 0.15 * frame.glow * alpha));
  glow.addColorStop(1, withAlpha(glowColour, 0));
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, size, size);

  ctx.beginPath();
  const points = 72;
  for (let i = 0; i <= points; i += 1) {
    const angle = (i / points) * TAU;
    const r = bubbleRadius(frame, angle) * half;
    // Canvas y runs down; the shape's angles run anticlockwise, as the shader's do.
    const x = half + Math.cos(angle) * r;
    const y = half - Math.sin(angle) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();

  ctx.globalAlpha = alpha;
  const inside = hues.pearl;
  const band = muted
    ? [palette.line, palette.line, palette.line]
    : [hues.accent, hues.deep, hues.tint];
  const glass = ctx.createRadialGradient(half, half, 0, half, half, body * 1.08);
  glass.addColorStop(0, withAlpha(inside, 0.95));
  glass.addColorStop(0.62, withAlpha(hues.bright, 0.35 + 0.55 * (dark ? 1 : 0.9)));
  glass.addColorStop(0.78, withAlpha(band[0]!, 0.85 * Math.min(1, frame.light)));
  glass.addColorStop(0.88, withAlpha(band[1]!, 0.9));
  glass.addColorStop(0.96, withAlpha(band[2]!, 0.95));
  glass.addColorStop(1, withAlpha(dark ? hues.pearl : hues.shade, 0.9));
  ctx.fillStyle = glass;
  ctx.fill();

  ctx.save();
  ctx.clip();
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(1, body * 0.1);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
  ctx.beginPath();
  ctx.arc(half, half, body * 0.9, Math.PI * 1.08, Math.PI * 1.36);
  ctx.stroke();
  ctx.restore();
  ctx.globalAlpha = 1;
}

/** A CSS colour with its alpha replaced: `#rgb`, `#rrggbb`, `rgb()` and `rgba()`; else as given. */
export function withAlpha(color: string, alpha: number): string {
  const rgb = rgbOf(color);
  const a = Math.round(clamp01(alpha) * 1000) / 1000;
  return rgb ? `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${a})` : color.trim();
}

/** A CSS colour as three channels 0 to 255, or null for anything but hex and rgb(). */
export function rgbOf(color: string): [number, number, number] | null {
  const text = color.trim();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(text);
  if (hex) {
    const digits = hex[1]!.length === 3 ? [...hex[1]!].map((d) => d + d).join('') : hex[1]!;
    const n = parseInt(digits, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  const rgb = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i.exec(text);
  return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null;
}

/** The orb's colours from the tokens in force on an element, falling back per token. */
export function paletteOf(style: { getPropertyValue(name: string): string }): OrbPalette {
  const read = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
  return {
    ink: read('--accent-ink', ORB_FALLBACK_PALETTE.ink),
    bright: read('--accent-bright', ORB_FALLBACK_PALETTE.bright),
    accent: read('--accent', ORB_FALLBACK_PALETTE.accent),
    deep: read('--accent-deep', ORB_FALLBACK_PALETTE.deep),
    line: read('--ink-3', ORB_FALLBACK_PALETTE.line),
    surface: read('--bg-2', ORB_FALLBACK_PALETTE.surface),
  };
}
